// src/utilities/number/format.ts
import { formatFloat } from './formatFloat'

const numberFormatSteps = [
  [1_000_000_000, 'b'],
  [1_000_000, 'm'],
  [1_000, 'k']
] as const

/**
 * Formats a number with an abbreviation e.g. 1000 -> 1k.
 *
 * @param number the number to format
 * @param maximumSignificantDigits the number of significant digits to include
 * @param includeDecimals when true, formatted number will always include non trailing zero decimal places
 */
export function formatAbbreviatedNumber(
  number: number,
  maximumSignificantDigits: number,
  includeDecimals: boolean
): string {
  number = Number(number)

  const prefix = number < 0 ? '-' : ''
  const numAbsValue = Math.abs(number)

  for (const step of numberFormatSteps) {
    const [suffixNum, suffix] = step
    const shortValue = Math.floor(numAbsValue / suffixNum)
    const fitsBound = numAbsValue % suffixNum === 0

    if (shortValue <= 0) {
      continue
    }

    const useShortValue = !includeDecimals && (shortValue > 10 || fitsBound)

    if (useShortValue) {
      if (maximumSignificantDigits === undefined) {
        return `${prefix}${shortValue}${suffix}`
      }
      const formattedNumber = parseFloat(shortValue.toPrecision(maximumSignificantDigits)).toString()
      return `${prefix}${formattedNumber}${suffix}`
    }

    const formattedNumber = formatFloat(numAbsValue / suffixNum, maximumSignificantDigits || 1).toLocaleString(
      undefined,
      {
        maximumSignificantDigits
      }
    )

    return `${prefix}${formattedNumber}${suffix}`
  }

  return number.toLocaleString(undefined, { maximumSignificantDigits })
}
